import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/joy";
import ContentTemplateV2 from "../../components/task/content/ContentTemplateV2";
import NumericKeypad, { Dots } from "../../components/security/NumericKeypad";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  appLockEnabledAtom,
  appLockPinHashAtom,
  PIN_SET_PLACEHOLDER,
  appBiometricEnabledAtom,
} from "recoil/appLockAtoms";
import { hashPin, isValidPin } from "utils/pinLock";
import { theme } from "styles/theme";
import { useSetAppLockPin, useUpdateBiometricEnabled } from "api/appLockQueries";
import useGoBack from "hooks/useGoBack";
import TaskHeader from "../../components/task/header/TaskHeader";
import { userAtom } from "../../recoil/userAtom";
import useGetDeviceInfo from "../../hooks/useGetDeviceInfo";
import FloatingArea from "../../components/common/FloatingArea";
import { nativeBridge } from "../../utils/nativeBridge";
import { toast } from "sonner";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import { useLocation, useNavigate } from "react-router-dom";
import FullPageContainer from "../container/FullPageContainer";
import BackButton from "../../components/common/BackButton";
import { extractHexColor } from "../../styles/hexOpacity";
import { sendMessage } from "../../utils/webview";
import CustomModal from "../../components/common/CustomModal";

/**
 * @deprecated
 * TODO PinSetupTask와 통합
 */
const AppLockSetupPage: React.FC = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const [pin1, setPin1] = useState<string>("");
  const [pin2, setPin2] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false);

  const user = useRecoilValue(userAtom);
  const setPinHash = useSetRecoilState(appLockPinHashAtom);
  const setEnabled = useSetRecoilState(appLockEnabledAtom);
  const setPinMutation = useSetAppLockPin();
  const updateBioMutation = useUpdateBiometricEnabled();
  const setBioEnabled = useSetRecoilState(appBiometricEnabledAtom);
  const goBack = useGoBack();
  const { homeIndicatorHeight } = useGetDeviceInfo();
  const isInappWebview = useIsInappWebview();
  const location = useLocation();
  const navigate = useNavigate();
  const redirectTo = React.useMemo(() => {
    let target: string | null = null;
    const state = location.state as any;
    if (state && typeof state.redirectTo === "string") {
      target = state.redirectTo;
    }
    if (!target) {
      const qs = new URLSearchParams(location.search);
      const q = qs.get("redirect");
      if (q) target = q;
    }
    // allow only internal paths like "/..." and prevent protocol-relative "//..."
    if (target && /^\//.test(target) && !/^\/\//.test(target)) {
      return target;
    }
    return null;
  }, [location.state, location.search]);

  // Layout measurement refs
  const headerRef = useRef<HTMLDivElement | null>(null);
  const floatingRef = useRef<HTMLDivElement | null>(null);
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  // Measure available vertical space between header and keypad
  React.useEffect(() => {
    const measure = () => {
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const headerH = headerRef.current?.offsetHeight ?? 0;
      const floatingH = floatingRef.current?.offsetHeight ?? 0;
      const avail = Math.max(0, viewport - headerH - floatingH);
      setAvailableHeight(avail);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, []);

  const current = step === 1 ? pin1 : pin2;

  const helpText = useMemo(() => {
    if (error) return error;
    return step === 1
      ? "사용할 비밀번호를 입력해주세요."
      : "한 번 더 입력해주세요.";
  }, [step, error]);

  const onInput = useCallback(
    (digit: string) => {
      setError("");
      if (!/^\d$/.test(digit)) return;
      if (current.length >= 4) return;
      if (step === 1) {
        setPin1((prev) => (prev + digit).slice(0, 4));
      } else {
        setPin2((prev) => (prev + digit).slice(0, 4));
      }
    },
    [current.length, step]
  );

  const onBackspace = useCallback(() => {
    setError("");
    if (step === 1) {
      setPin1((prev) => prev.slice(0, -1));
    } else {
      setPin2((prev) => prev.slice(0, -1));
    }
  }, [step]);

  const onClear = useCallback(() => {
    setError("");
    if (step === 1) {
      setPin1("");
    } else {
      setPin2("");
    }
  }, [step]);

  const onSuccessConfirm = useCallback(() => {
    setSuccessModalOpen(false);
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
    } else {
      goBack();
    }
  }, [redirectTo, navigate, goBack]);

  // 4자리 다 입력될 때 처리
  React.useEffect(() => {
    const run = async () => {
      if (current.length !== 4) return;
      if (!isValidPin(current)) {
        setError("비밀번호는 숫자 4자리여야 합니다.");
        onClear();
        return;
      }
      if (step === 1) {
        setStep(2);
      } else {
        if (pin1 !== pin2) {
          setError("비밀번호가 일치하지 않아요.");
          // Reset to step 1 and clear both inputs to restart setup
          setPin1("");
          setPin2("");
          setStep(1);
          return;
        }
        // 저장 (서버에 해시 전달)
        try {
          const hash = await hashPin(pin2);
          const res = await setPinMutation.mutateAsync({
            pinHash: hash,
            isEnable: true,
          });
          setPinHash(res.hasPin ? PIN_SET_PLACEHOLDER : null);
          setEnabled(res.isEnabled);

          // PIN 설정 이후: 기기에서 생체 인증을 지원하면 이어서 설정 안내
          try {
            const info = await nativeBridge.getBiometricStatus(3500);
            if (info?.available) {
              const bioRes = await nativeBridge.requestBiometric({
                promptMessage: "생체 인증을 활성화합니다",
                cancelLabel: "나중에",
                disableDeviceFallback: false,
              });
              if (bioRes?.success) {
                const prevSet = true;
                setBioEnabled(prevSet);
                try {
                  const data = await updateBioMutation.mutateAsync(true);
                  if (typeof (data as any)?.isBiometricEnabled === "boolean") {
                    setBioEnabled(!!(data as any).isBiometricEnabled);
                  }
                } catch {
                  // 서버 반영 실패 시 로컬 롤백
                  setBioEnabled(false);
                }
                toast.success("생체 인증이 활성화되었습니다.");
              } else if (info && info.available && info.enrolled === false) {
                toast.error("생체 정보가 등록되어 있지 않습니다. 설정에서 등록 후 다시 시도하세요.");
              }
            }
          } catch {
            // 생체 인증 확인/요청 중 오류는 무시하고 진행
          }

          setSuccessModalOpen(true);
          return;
        } catch (e) {
          setError("비밀번호 설정에 실패했습니다. 잠시 후 다시 시도하세요.");
          setPin2("");
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    if (isInappWebview) {
      const topBackground = extractHexColor(
        theme.vars.palette.Background.Level1
      );
      const bottomBackground = extractHexColor(
        theme.vars.palette.Background.Level1
      );

      if (topBackground && bottomBackground) {
        sendMessage(
          JSON.stringify({
            event: "set_safearea_color",
            params: {
              topColor: `${topBackground}`,
              bottomColor: `${bottomBackground}`,
            },
          })
        );
      }
    }
  }, []);

  const child = (
    <Stack
      spacing={4}
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: availableHeight ? `${availableHeight}px` : "60vh" }}
    >
      <Stack spacing={1} alignItems="center">
        <Typography
          variant={"Body"}
          sx={{
            fontWeight: 500,
            color: theme.vars.palette.Content.Medium,
          }}
        >
          {helpText}
        </Typography>
      </Stack>

      <Stack spacing={3} alignItems="center" sx={{ mt: 2 }}>
        <Dots count={current.length} />
      </Stack>
    </Stack>
  )
  return (
    <>
      <FullPageContainer backgroundColor={theme.vars.palette.Background.Level1}>
        <Stack
          direction={"column"}
          justifyContent={"flex-start"}
          sx={{
            maxWidth: "660px",
            width: "100%",
            mx: "auto",
            height: "100vh",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              position: "sticky",
              top: 0,
              zIndex: 100,
              height: `60px`, // 헤더 높이 설정
              px: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <BackButton onBack={() => navigate(-1)} />
              <Typography
                variant="Body"
                sx={{
                  px: 0,
                  fontColor: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {"비밀번호 설정"}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            sx={{
              width: "100%",
              flex: 1,
              overflow: "auto",
              px: `20px`,
              py: 0,
              position: "relative",
            }}
          >
            {child}
          </Stack>
        </Stack>
        <FloatingArea noDivider={true} fullWidth={true} ref={floatingRef}>
          <NumericKeypad
            onInput={onInput}
            onBackspace={onBackspace}
            onClear={onClear}
          />
          <Box sx={{ height: "40px" }} />
        </FloatingArea>

        <CustomModal
          open={successModalOpen}
          onClose={onSuccessConfirm}
          title={"설정 완료"}
          subtitle={"이제 앱을 열 때 비밀번호를 물을게요."}
          actions={[
            {
              label: "확인",
              onClick: onSuccessConfirm,
              variant: "Solid",
            },
          ]}
        />
      </FullPageContainer>
    </>
  );
};

export default AppLockSetupPage;
